<template>
  <div class="h-full overflow-scroll p-4 fade-in max-w-screen-xl w-full mx-auto">
    <div class="w-full max-w-screen-2xl mx-auto flex flex-col">
      <router-link
        to="/manage"
        class="text-xs text-gray-500 hover:text-gray-700 flex items-center mb-2 rounded-lg hover:bg-gray-200 w-fit py-2 px-1 transition-all duration-200"
      >
        <ArrowLeft class="w-4 h-4 mr-2" />
        Back to Expeditions
      </router-link>
      <div class="flex items-center mb-4">
        <BadgePlus class="w-5 h-5s mr-2" />
        <h1 class="text-xl font-normal cursor-default">Create New Expedition</h1>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Left Column: Expedition Form -->
        <div class="bg-white p-4 px-6 rounded-xl border-[1px] border-gray-200">
          <!-- Use the new NewExpeditionForm component -->
          <NewExpeditionForm
            :handle-submit="onSubmit"
            :expedition-types="expeditionsStore.expeditionTypes"
            :set-field-value="setFieldValue as SetFieldValuePropType"
          />
        </div>

        <div class="flex flex-col gap-4">
          <div class="bg-white p-4 px-6 rounded-xl border-[1px] border-gray-200 space-y-4">
            <div class="flex items-center gap-2">
              <h2 class="text-lg">Add Fellows</h2>
            </div>
            <UserSelection v-model="selectedUsers" />
          </div>

          <div class="bg-white p-4 px-6 rounded-xl border-[1px] border-gray-200">
            <div class="flex items-center gap-2">
              <h2 class="text-lg">Choose Main Guide</h2>
            </div>
            <p class="text-sm text-gray-500 mb-4">
              Select a main guide for this expedition
            </p>
            <MainGuideSelection v-model="selectedMainGuideId" :guides="selectedGuides" />
          </div>

          <div class="bg-white p-4 px-6 rounded-xl border-[1px] border-gray-200">
            <div class="flex items-center gap-2">
              <h2 class="text-lg">Add Guides</h2>
            </div>
            <p class="text-sm text-gray-500 mb-4">
              You will be added to the list of guides automatically since you are the creator
            </p>
            <GuideSelection v-model="selectedGuides" />
          </div>
        </div>
      </div>
      <button
        @click="onSubmit"
        :disabled="expeditionsStore.isLoading"
        class="w-fit text-xs mt-2 ml-auto bg-cosmicPurple text-white p-2 px-4 rounded-md hover:bg-cosmicPurple/80 hover:-translate-y-[1px] transition-all duration-200 flex items-center justify-center"
      >
        <Loader2 v-if="expeditionsStore.isLoading" class="w-4 h-4 mr-2 animate-spin" />
        Create Expedition
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useExpeditionsStore } from '@/stores/expeditions'
import { BadgePlus, Loader2, ArrowLeft } from 'lucide-vue-next'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { format } from 'date-fns'
import { useToast } from 'primevue/usetoast'
import UserSelection from '@/components/manage/UserSelection.vue'
import GuideSelection from '@/components/manage/GuideSelection.vue'
import NewExpeditionForm from '@/components/manage/NewExpeditionForm.vue'
import { useRouter } from 'vue-router'
import type { User } from '@/types/user'
import MainGuideSelection from '@/components/manage/MainGuideSelection.vue'

// Define the type alias for the prop
type SetFieldValuePropType = (field: string, value: any) => void

const expeditionsStore = useExpeditionsStore()
const router = useRouter()
const formSchema = toTypedSchema(
  z.object({
    name: z.string().min(2, { message: 'Name must be at least 2 characters.' }).max(50),
    details: z.string().optional(),
    date: z.date({ required_error: 'A start date is required.' }),
    type: z.string().optional(),
    channel: z.string().optional(),
    path_id: z.string({ required_error: 'Please select a path.' }),
    new_path: z.boolean().default(false)
  })
)

const { handleSubmit, setFieldValue } = useForm({
  validationSchema: formSchema,
  initialValues: {
    new_path: false
  }
})

const selectedUsers = ref<User[]>([])
const selectedGuides = ref<User[]>([])
const selectedMainGuideId = ref<string | null>(null)
const toast = useToast()

onMounted(() => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  expeditionsStore.fetchExpeditionTypes()
})

const onSubmit = handleSubmit(async (values) => {
  const startDate = values.date instanceof Date ? format(values.date, 'yyyy-MM-dd') : null
  
  console.log('Form values before payload:', values) // Debug log
  
  const payload = {
    name: values.name,
    expedition_type: values.type ? parseInt(values.type, 10) : undefined,
    start_date: startDate || '',
    details: values.details,
    slack_stream_channel: values.channel ? `#${values.channel}` : undefined,
    adventurers: selectedUsers.value.map((user) => user.id),
    guides: selectedGuides.value.map((guide) => guide.id),
    path_id: values.path_id ? parseInt(values.path_id, 10) : undefined,
    new_path: Boolean(values.new_path),
    main_guide: selectedMainGuideId.value ? Number(selectedMainGuideId.value) : undefined
  }

  console.log('Final payload:', payload) // Debug log
  try {
    await expeditionsStore.createExpedition(payload)
    if (!expeditionsStore.error) {
      toast.add({
        severity: 'success',
        summary: 'Expedition created successfully!',
        detail: 'You can now start the expedition.',
        life: 3000
      })
      setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.push(`/manage/`)
      }, 1500)
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error creating expedition',
        detail: expeditionsStore.error ?? 'An unknown error occurred.'
      })
    }
  } catch (error) {
    console.error('Error creating expedition:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to create expedition. Please try again.'
    })
  }
})
</script>